/**
 * Veluna Quiz Popup — concern-first email capture with resilient exit intent
 * Surfaces: homepage + blog articles only
 * Flow: pick sleep concern (1 of 4) -> email capture -> ritual PDF guide opens
 * Resilient: closing the popup does NOT permanently dismiss it. Only an actual
 * email capture (or explicit "Don't show again") suppresses future fires.
 */
(function () {
  'use strict';

  const CONFIG = {
    backend: {
      url: 'https://ary09mimgg.execute-api.us-east-2.amazonaws.com/Prod/subscribe',
      sequence: 'welcome',
      popupVariant: 'quiz_v1'
    },
    timing: {
      dwellMs: 45000,
      cooldownMs: 60000,
      scrollThreshold: 0.5
    },
    storage: {
      captured: 'veluna_email_captured',
      dismissed: 'veluna_popup_dismissed_forever',
      lastShown: 'veluna_popup_last_shown'
    },
    concerns: [
      { id: 'mc', label: 'Racing Thoughts & Stress',     ritual: 'Mind Calm',       product: '/products/mind-calm-ritual-set',         guide: 'https://cdn.shopify.com/s/files/1/0660/1372/4750/t/706/assets/Veluna-Mind-Calm-Sleep-Guide.pdf?v=1783624257' },
      { id: 'dr', label: 'Body Tension & Restlessness',  ritual: 'Deep Restore',    product: '/products/deep-restore-ritual-set',      guide: 'https://cdn.shopify.com/s/files/1/0660/1372/4750/t/706/assets/Veluna-Deep-Restore-Sleep-Guide.pdf?v=1783624258' },
      { id: 'ss', label: 'Self-Care & Transformation',   ritual: 'Sacred Soothe',   product: '/products/sacred-soothe-ritual-set-1',   guide: 'https://cdn.shopify.com/s/files/1/0660/1372/4750/t/706/assets/Veluna-Sacred-Soothe-Sleep-Guide.pdf?v=1783624259' },
      { id: 'cr', label: 'Sleep Schedule & Timing',      ritual: 'Circadian Reset', product: '/products/circadian-reset-ritual-set',   guide: 'https://cdn.shopify.com/s/files/1/0660/1372/4750/t/706/assets/Veluna-Circadian-Reset-Sleep-Guide.pdf?v=1783624260' }
    ]
  };

  // ---------- state ----------
  let popupEl = null;
  let selected = null;
  let isOpen = false;
  let dwellTimer = null;
  let triggersInstalled = false;

  // ---------- storage helpers ----------
  function ls(key) { try { return localStorage.getItem(key); } catch (_) { return null; } }
  function lsSet(key, val) { try { localStorage.setItem(key, val); } catch (_) {} }
  function isCaptured() { return ls(CONFIG.storage.captured) === 'true'; }
  function isPermanentlyDismissed() { return ls(CONFIG.storage.dismissed) === 'true'; }
  function inCooldown() {
    const last = parseInt(ls(CONFIG.storage.lastShown) || '0', 10);
    return last && (Date.now() - last) < CONFIG.timing.cooldownMs;
  }
  function recordShown() { lsSet(CONFIG.storage.lastShown, String(Date.now())); }

  // ---------- surface gate ----------
  function isAllowedSurface() {
    const p = window.location.pathname;
    if (p.includes('/checkout') || p.includes('/cart')) return false;
    const isHome = p === '/' || p === '/index' || p === '';
    const isBlogArticle = /^\/blogs\/[^/]+\/[^/]+/.test(p);
    return isHome || isBlogArticle;
  }

  function shouldShow() {
    if (!isAllowedSurface()) return false;
    /* Same precedence rule as dispatch(): this engine owns a page only when no
       page-specific capture has claimed it. Gating dispatch() alone was not
       enough, because this function drives the engine's OWN overlay and that
       path could still open alongside a page-specific capture. */
    var claimed = (window.__vlnPageSpecific || []).filter(function (id) {
      return id && id !== 'pdf-capture';
    });
    if (claimed.length) return false;
    if (window.VelunaCaptureGuard &&
        window.VelunaCaptureGuard.hasPageSpecific('pdf-capture')) return false;
    /* VELUNA_CAPTURE_GUARD: purchase configuration and in-flight purchases
       outrank email capture. Policy lives in veluna-capture-guard.js. */
    if (window.VelunaCaptureGuard && !window.VelunaCaptureGuard.mayShow('pdf-capture')) return false;

    if (isCaptured()) return false;
    if (isPermanentlyDismissed()) return false;
    if (isOpen) return false;
    if (inCooldown()) return false;
    return true;
  }

  // ---------- styles ----------
  function injectStyles() {
    if (document.getElementById('vqp-styles')) return;
    const css = `
.vqp__overlay { position: fixed; inset: 0; background: rgba(15,20,40,0.78); z-index: 999999; display: flex; align-items: center; justify-content: center; padding: 20px; opacity: 0; transition: opacity .25s ease; backdrop-filter: blur(4px); }
.vqp__overlay.is-open { opacity: 1; }
.vqp__card { background: #fdfaf3; color: #1a1f35; max-width: 520px; width: 100%; border-radius: 16px; padding: 36px 32px; box-shadow: 0 24px 60px rgba(0,0,0,.35); position: relative; transform: translateY(12px); transition: transform .25s ease; font-family: 'Inter', system-ui, -apple-system, sans-serif; max-height: 92vh; overflow-y: auto; }
.vqp__overlay.is-open .vqp__card { transform: translateY(0); }
.vqp__close { position: absolute; top: 14px; right: 14px; background: transparent; border: 0; cursor: pointer; padding: 8px; color: #1a1f35; opacity: .55; }
.vqp__close:hover { opacity: 1; }
.vqp__eyebrow { font-size: 11px; letter-spacing: .18em; text-transform: uppercase; color: #c9a86c; margin: 0 0 8px; font-weight: 600; }
.vqp__h { font-family: 'Playfair Display', Georgia, serif; font-size: 28px; line-height: 1.2; margin: 0 0 10px; font-weight: 600; }
.vqp__h em { color: #c9a86c; font-style: normal; }
.vqp__sub { font-size: 14.5px; line-height: 1.55; color: #4a5168; margin: 0 0 22px; }
.vqp__opts { display: grid; gap: 10px; margin: 0 0 18px; }
.vqp__opt { display: flex; align-items: center; gap: 12px; padding: 14px 16px; background: #fff; border: 1.5px solid rgba(26,31,53,.12); border-radius: 10px; cursor: pointer; text-align: left; font: inherit; color: #1a1f35; transition: all .15s ease; }
.vqp__opt:hover { border-color: #c9a86c; transform: translateX(2px); }
.vqp__opt-num { width: 26px; height: 26px; border-radius: 50%; background: #1a1f35; color: #fdfaf3; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; flex: 0 0 auto; }
.vqp__opt-label { flex: 1; font-size: 14.5px; font-weight: 500; }
.vqp__opt-arrow { color: #c9a86c; font-size: 18px; }
.vqp__form { display: flex; flex-direction: column; gap: 10px; }
.vqp__input { width: 100%; padding: 14px 16px; border: 1.5px solid rgba(26,31,53,.18); border-radius: 10px; font-size: 15px; font-family: inherit; background: #fff; color: #1a1f35; box-sizing: border-box; }
.vqp__input:focus { outline: none; border-color: #c9a86c; }
.vqp__btn { background: #1a1f35; color: #fdfaf3; border: 0; padding: 15px 20px; border-radius: 10px; font-size: 15px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all .15s ease; }
.vqp__btn:hover { background: #2a3050; }
.vqp__btn[disabled] { opacity: .6; cursor: wait; }
.vqp__legal { font-size: 11px; color: #6b7290; text-align: center; margin: 12px 0 0; line-height: 1.5; }
.vqp__legal a, .vqp__skip { color: #6b7290; text-decoration: underline; cursor: pointer; }
.vqp__skip { background: transparent; border: 0; font-size: 12px; padding: 8px; margin: 14px auto 0; display: block; font-family: inherit; }
.vqp__success { text-align: center; padding: 10px 0; }
.vqp__success-icon { width: 56px; height: 56px; border-radius: 50%; background: #c9a86c; color: #fff; display: inline-flex; align-items: center; justify-content: center; margin: 0 0 18px; font-size: 28px; }
.vqp__error { color: #b00020; font-size: 13px; margin: 8px 0 0; min-height: 18px; }
@media (max-width: 480px) { .vqp__card { padding: 28px 22px; } .vqp__h { font-size: 24px; } }
`;
    const style = document.createElement('style');
    style.id = 'vqp-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ---------- views ----------
  function viewConcerns() {
    return `
      <p class="vqp__eyebrow">Free • 14-page guide</p>
      <h2 class="vqp__h">Get your <em>personalized</em> sleep ritual guide</h2>
      <p class="vqp__sub">Which sleep challenge sounds most like you right now?</p>
      <div class="vqp__opts">
        ${CONFIG.concerns.map((c, i) => `
          <button class="vqp__opt" data-concern="${c.id}" type="button">
            <span class="vqp__opt-num">${i + 1}</span>
            <span class="vqp__opt-label">${c.label}</span>
            <span class="vqp__opt-arrow">›</span>
          </button>
        `).join('')}
      </div>
      <button class="vqp__skip" data-skip type="button">No thanks, just let me browse</button>
    `;
  }

  function viewEmail() {
    const c = selected;
    return `
      <p class="vqp__eyebrow">Step 2 of 2</p>
      <h2 class="vqp__h">Your <em>${c.ritual}</em> guide is ready</h2>
      <p class="vqp__sub">Where should we send your personalized sleep ritual guide and the science behind it?</p>
      <form class="vqp__form" data-form>
        <input class="vqp__input" type="email" name="email" placeholder="you@example.com" required autocomplete="email" />
        <button class="vqp__btn" type="submit" data-submit>Send my free guide</button>
        <p class="vqp__error" data-error></p>
      </form>
      <p class="vqp__legal">No spam. Unsubscribe anytime. By submitting, you agree to receive emails from Veluna.</p>
    `;
  }

  function viewSuccess() {
    const c = selected;
    const mobile = isMobileDevice();
    const sub = mobile
      ? `We just emailed your <strong>${c.ritual}</strong> guide. Tap below to open it now — or find it waiting in your inbox.`
      : `Your <strong>${c.ritual}</strong> guide just opened in a new tab and we emailed you a copy too. Want to skip ahead?`;
    return `
      <div class="vqp__success">
        <div class="vqp__success-icon">✓</div>
        <h2 class="vqp__h">Check your inbox</h2>
        <p class="vqp__sub">${sub}</p>
        <a class="vqp__btn" href="${c.guide}" target="_blank" rel="noopener" style="display:block;text-decoration:none;margin-bottom:12px;">Open your guide</a>
        <a href="${c.product}" style="display:inline-block;text-decoration:none;color:#1a1f35;font-size:14px;font-weight:600;opacity:.75;">Explore the ${c.ritual} ritual →</a>
      </div>
    `;
  }

  // ---------- popup ----------
  function buildPopup() {
    if (popupEl) return popupEl;
    injectStyles();
    const overlay = document.createElement('div');
    overlay.className = 'vqp__overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = `
      <div class="vqp__card">
        <button class="vqp__close" data-close aria-label="Close">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
        <div class="vqp__body" data-body></div>
      </div>
    `;
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closePopup('backdrop');
    });
    overlay.querySelector('[data-close]').addEventListener('click', () => closePopup('close-btn'));
    overlay.querySelector('[data-body]').addEventListener('click', (e) => {
      const opt = e.target.closest('[data-concern]');
      if (opt) { onConcernSelect(opt.getAttribute('data-concern')); return; }
      if (e.target.closest('[data-skip]')) { closePopup('skip'); return; }
    });
    overlay.querySelector('[data-body]').addEventListener('submit', (e) => {
      if (e.target.matches('[data-form]')) { e.preventDefault(); onEmailSubmit(e.target); }
    });
    document.body.appendChild(overlay);
    popupEl = overlay;
    return popupEl;
  }

  function render(html) {
    const body = popupEl.querySelector('[data-body]');
    body.innerHTML = html;
  }

  function showPopup(reason) {
    if (!shouldShow()) return;
    buildPopup();
    selected = null;
    render(viewConcerns());
    try { window.VelunaCaptureGuard && window.VelunaCaptureGuard.notifyOpen('pdf-capture'); } catch (_) {}
    requestAnimationFrame(() => popupEl.classList.add('is-open'));
    document.body.style.overflow = 'hidden';
    isOpen = true;
    recordShown();
    try {
      if (window.dataLayer) window.dataLayer.push({ event: 'veluna_popup_shown', trigger: reason });
    } catch (_) {}
  }

  function closePopup(reason) {
    if (!popupEl) return;
    popupEl.classList.remove('is-open');
    document.body.style.overflow = '';
    isOpen = false;
    /* Release the arbitration lock. Without this the guard kept reporting this
       engine as the owner after dismissal, which both blocked other surfaces
       and let this engine re-open itself (an owner never suppresses itself). */
    try { window.VelunaCaptureGuard && window.VelunaCaptureGuard.notifyClosed('pdf-capture'); } catch (_) {}
    /* Start the cooldown explicitly. `recordShown` is declared twice in this
       file and the later surface-keyed definition shadows the simple one that
       inCooldown() reads, so the cooldown key was never written and a dismissed
       popup re-fired immediately on an already-scrolled page. */
    try { lsSet(CONFIG.storage.lastShown, String(Date.now())); } catch (_) {}
    setTimeout(() => { if (popupEl) popupEl.remove(); popupEl = null; }, 250);
    // Resilient: do NOT mark dismissed; just respect cooldown then re-arm triggers
    rearmTriggers();
  }

  function onConcernSelect(id) {
    selected = CONFIG.concerns.find((c) => c.id === id);
    if (!selected) return;
    render(viewEmail());
    setTimeout(() => {
      const inp = popupEl && popupEl.querySelector('input[type="email"]');
      if (inp) inp.focus();
    }, 50);
  }

  function onEmailSubmit(form) {
    const input = form.querySelector('input[type="email"]');
    const errEl = form.querySelector('[data-error]');
    const btn = form.querySelector('[data-submit]');
    const email = (input.value || '').trim();
    errEl.textContent = '';
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errEl.textContent = 'Please enter a valid email.';
      return;
    }
    btn.disabled = true;
    btn.textContent = 'Sending…';
    submitToBackend(email)
      .then((res) => {
        if (res === null || !res.ok) throw new Error('network');
        lsSet(CONFIG.storage.captured, 'true');
        deliverGuide(selected.guide);
        render(viewSuccess());
        try {
          if (window.dataLayer) window.dataLayer.push({
            event: 'veluna_lead_captured',
            sleep_concern: selected.id,
            ritual_type: selected.ritual
          });
        } catch (_) {}
      })
      .catch(() => {
        errEl.textContent = 'Something went wrong. Please try again.';
        btn.disabled = false;
        btn.textContent = 'Send my free guide';
      });
  }

  // Detect small-screen / touch devices. On these, auto-opening a raw
  // cross-origin PDF in a new tab traps the user in the browser's PDF viewer
  // with no easy way back to the store, so we let them open it explicitly.
  function isMobileDevice() {
    try {
      return window.matchMedia('(max-width: 768px)').matches ||
        /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
    } catch (_) { return false; }
  }

  // Deliver the guide. On desktop we open the PDF in a new tab AND force a
  // download. On mobile we do NOT auto-open (that traps the user in the PDF
  // viewer with no clear way back); the success view shows an explicit
  // "Open your guide" button and a copy is emailed. Silent blob download is
  // still attempted on both so the guide is never lost.
  function deliverGuide(url) {
    if (!isMobileDevice()) {
      try { window.open(url, '_blank', 'noopener'); } catch (_) {}
    }
    try {
      var filename = (url.split('/').pop() || 'Veluna-Sleep-Guide.pdf').split('?')[0];
      fetch(url, { mode: 'cors' })
        .then(function (r) { return r && r.ok ? r.blob() : null; })
        .then(function (blob) {
          if (!blob) return;
          var objUrl = URL.createObjectURL(blob);
          var a = document.createElement('a');
          a.href = objUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(function () { URL.revokeObjectURL(objUrl); }, 4000);
        })
        .catch(function () {});
    } catch (_) {}
  }

  function submitToBackend(email) {
    return fetch(CONFIG.backend.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        topic: 'popup_subscribe',
        source: 'veluna_quiz_popup',
        email_sequence: CONFIG.backend.sequence,
        popup_variant: CONFIG.backend.popupVariant,
        tags: ['veluna_quiz_popup', 'concern_' + selected.id, CONFIG.backend.sequence],
        fields: {
          sleep_concern: selected.id,
          ritual_type: selected.ritual,
          email_sequence: CONFIG.backend.sequence,
          ritual_guide_url: selected.guide,
          popup_variant: CONFIG.backend.popupVariant
        },
        metadata: {
          page: window.location.pathname,
          referrer: document.referrer || '',
          user_agent: (navigator && navigator.userAgent) || ''
        }
      })
    }).catch(() => null);
  }

  // ---------- triggers ----------
  function installTriggers() {
    if (triggersInstalled) return;
    triggersInstalled = true;

    // 1) scroll past threshold
    const onScroll = () => {
      if (!shouldShow()) return;
      const h = document.documentElement;
      const scrolled = (h.scrollTop + window.innerHeight) / h.scrollHeight;
      if (scrolled >= CONFIG.timing.scrollThreshold) {
        showPopup('scroll');
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // 2) exit intent (desktop)
    const onMouseLeave = (e) => {
      if (e.clientY <= 0 && shouldShow()) showPopup('exit-intent');
    };
    document.addEventListener('mouseleave', onMouseLeave);

    // 3) dwell timer
    if (dwellTimer) clearTimeout(dwellTimer);
    dwellTimer = setTimeout(() => {
      if (shouldShow()) showPopup('dwell');
    }, CONFIG.timing.dwellMs);
  }

  function rearmTriggers() {
    // After close, reset dwell so popup can re-fire after cooldown.
    if (dwellTimer) clearTimeout(dwellTimer);
    dwellTimer = setTimeout(() => {
      if (shouldShow()) showPopup('dwell-rearm');
    }, CONFIG.timing.dwellMs);
  }

  // ---------- init ----------
  function init() {
    if (!isAllowedSurface()) return;
    if (isCaptured() || isPermanentlyDismissed()) return;
    installTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Test hook for QA
  window.__velunaPopupTest = function () {
    try { localStorage.removeItem(CONFIG.storage.captured); } catch (_) {}
    try { localStorage.removeItem(CONFIG.storage.dismissed); } catch (_) {}
    try { localStorage.removeItem(CONFIG.storage.lastShown); } catch (_) {}
    showPopup('manual-test');
  };
})();
/**
 * Veluna PDF Capture — unified email capture popup engine (Phase G).
 *
 * Replaces the fragmented set of:
 *   - veluna-homepage-popup.js
 *   - veluna-quiz-abandon.js
 *   - veluna-platform-aware-popup.js
 *   - veluna-popup-optimization.js
 *   - veluna-popup-templates.js
 *   - veluna-advanced-email.js
 *   - veluna-email-v3.js
 *   - veluna-email.js
 *
 * Architecture:
 *   1. Triggers are surface-specific (homepage / pdp / quiz / cart) and gated
 *      by the Unified Popup Manager's 30-min session lockout.
 *   2. Each trigger dispatches a `luna-popup-decision` event with a
 *      surface-aware payload. The unified manager renders.
 *   3. Templates are registered into `window.VelunaPopupTemplates` and use the
 *      $47 PDF guide as the universal hook (no mystery discount, IM8 tone).
 *   4. Submission posts to the Veluna subscribe API (API Gateway /Prod/subscribe)
 *      and emits a `veluna-email-captured` event for analytics.
 *
 * Frequency / dedupe rules:
 *   - Global: 30 minutes per session via UnifiedPopupManager.
 *   - Per surface: 1 per 7 days for homepage; 1 per session for pdp / cart;
 *     1 per session + 1 per 7 days global for quiz.
 *
 * Safe-to-load fallback: if UnifiedPopupManager is missing, the engine
 * silently no-ops so it never blocks the page.
 */
(function () {
  'use strict';

  if (window.__VELUNA_PDF_CAPTURE_LOADED__) return;
  window.__VELUNA_PDF_CAPTURE_LOADED__ = true;

  // ---------------------------------------------------------------------------
  // Storage keys + helpers
  // ---------------------------------------------------------------------------
  const STORE = {
    captured: 'veluna_pdf_captured',          // localStorage: email captured
    lastShown: 'veluna_pdf_last_shown',       // localStorage: per-surface ts
    sessionShown: 'veluna_pdf_session_shown', // sessionStorage: per-surface
  };
  const DAY = 24 * 60 * 60 * 1000;
  const FREQ = {
    homepage: { perDays: 7,  perSession: false },
    pdp:      { perDays: 0,  perSession: true  },
    quiz:     { perDays: 7,  perSession: true  },
    cart:     { perDays: 0,  perSession: true  },
  };

  function isCaptured() {
    try { return !!localStorage.getItem(STORE.captured); } catch (e) { return false; }
  }
  function markCaptured(email) {
    try { localStorage.setItem(STORE.captured, email + '|' + Date.now()); } catch (e) {}
  }
  function lastShown(surface) {
    try {
      const map = JSON.parse(localStorage.getItem(STORE.lastShown) || '{}');
      return Number(map[surface] || 0);
    } catch (e) { return 0; }
  }
  function recordShown(surface) {
    try {
      const map = JSON.parse(localStorage.getItem(STORE.lastShown) || '{}');
      map[surface] = Date.now();
      localStorage.setItem(STORE.lastShown, JSON.stringify(map));
      const sess = JSON.parse(sessionStorage.getItem(STORE.sessionShown) || '{}');
      sess[surface] = 1;
      sessionStorage.setItem(STORE.sessionShown, JSON.stringify(sess));
    } catch (e) {}
  }
  function sessionShown(surface) {
    try {
      const sess = JSON.parse(sessionStorage.getItem(STORE.sessionShown) || '{}');
      return !!sess[surface];
    } catch (e) { return false; }
  }
  function eligible(surface) {
    if (isCaptured()) return false;
    const rules = FREQ[surface] || FREQ.homepage;
    if (rules.perSession && sessionShown(surface)) return false;
    if (rules.perDays > 0) {
      const since = Date.now() - lastShown(surface);
      if (since < rules.perDays * DAY) return false;
    }
    // Global manager 30-min lockout
    if (window.unifiedPopupManager && window.unifiedPopupManager.hasShownThisSession &&
        window.unifiedPopupManager.hasShownThisSession()) return false;
    return true;
  }

  // ---------------------------------------------------------------------------
  // Surface detection
  // ---------------------------------------------------------------------------
  function detectSurface() {
    const t = (window.VelunaThemeSettings && window.VelunaThemeSettings.template) || '';
    const path = location.pathname;
    if (path === '/' || t === 'index') return 'homepage';
    if (path.indexOf('/products/') === 0 || t.indexOf('product') === 0) return 'pdp';
    if (path.indexOf('/pages/quiz') === 0 || /quiz|ritual-finder|sleep-quiz/.test(path)) return 'quiz';
    if (path.indexOf('/cart') === 0) return 'cart';
    return null;
  }

  // ---------------------------------------------------------------------------
  // Guide title resolver (drives PDP family-specific copy)
  // ---------------------------------------------------------------------------
  function resolveGuideTitle() {
    // Prefer explicit template hint set by my-ritual / PDP sections
    const hint = (window.VelunaThemeSettings && window.VelunaThemeSettings.popupVariant) || '';
    const map = {
      mind_calm_reset_guide:        'The Mind Calm Reset Guide',
      muscle_recovery_protocol:      'The Muscle Recovery Protocol',
      circadian_rhythm_handbook:     'The Circadian Rhythm Handbook',
      hormone_balance_blueprint:     'The Hormone Balance Blueprint',
      sleep_foundations_guide:       'The Sleep Foundations Guide',
    };
    if (hint && map[hint]) return map[hint];
    // PDP fallback by product handle
    if (location.pathname.indexOf('/products/') === 0) {
      const h = location.pathname.split('/').pop() || '';
      if (/mind-calm|theanine|magnesium-glycinate/.test(h)) return map.mind_calm_reset_guide;
      if (/muscle|recovery|threonate|magnesium-malate/.test(h)) return map.muscle_recovery_protocol;
      if (/circadian|reset|jetlag|melatonin|light/.test(h)) return map.circadian_rhythm_handbook;
      if (/hormone|cycle|woman|estrogen|progesterone/.test(h)) return map.hormone_balance_blueprint;
    }
    return map.sleep_foundations_guide;
  }

  // ---------------------------------------------------------------------------
  // Decision dispatcher
  // ---------------------------------------------------------------------------
  function dispatch(surface, reason) {
    if (!eligible(surface)) return;
    /* This path renders through UnifiedPopupManager and previously bypassed the
       capture guard entirely, so it could open a second intrusive overlay and
       could steal the lane from a page-specific engine. */
    var queued = (window.__vlnPageSpecific || []).filter(function (id) {
      return id && id !== 'pdf-capture';
    });
    if (queued.length) return;   // a dedicated capture owns this page type
    /* This engine has two rendering paths: its own .vqp__overlay and this
       delegated one. Only the first honoured the dismissal cooldown, so a
       visitor who closed the popup saw the delegated copy reappear seconds
       later. Both paths now share one cooldown. */
    if (isOpen || inCooldown()) return;
    if (window.VelunaCaptureGuard) {
      if (window.VelunaCaptureGuard.hasPageSpecific('pdf-capture')) return;
      if (!window.VelunaCaptureGuard.mayShow('pdf-capture')) return;
    }
    const guide = resolveGuideTitle();
    const decision = {
      // `popupType` is what UnifiedPopupManager.getTemplate() reads; keep
      // `type` for backwards-compat with anything else listening.
      popupType: 'pdf_capture',
      type: 'pdf_capture',
      /* The renderer must claim the guard as THIS engine, not as itself. */
      captureId: 'pdf-capture',
      surface: surface,
      reason: reason,
      payload: {
        guideTitle: guide,
        giftValue: 47,
        cta: 'Email me the guide',
        privacy: 'No spam. Unsubscribe anytime.',
      },
    };
    recordShown(surface);
    let displayed = false;
    if (window.unifiedPopupManager && typeof window.unifiedPopupManager.showPopup === 'function') {
      displayed = window.unifiedPopupManager.showPopup(decision) === true;
    } else {
      window.dispatchEvent(new CustomEvent('luna-popup-decision', { detail: decision }));
    }
    if (!displayed) {
      // Manager refused (e.g. already shown). Don't keep the surface locked.
      try {
        const map = JSON.parse(localStorage.getItem(STORE.lastShown) || '{}');
        delete map[surface];
        localStorage.setItem(STORE.lastShown, JSON.stringify(map));
      } catch (e) {}
    }
    if (window.VELUNA_ANALYTICS_ENDPOINT) {
      try {
        fetch(window.VELUNA_ANALYTICS_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'pdf_capture_shown',
            surface: surface,
            reason: reason,
            guide: guide,
            ts: Date.now(),
          }),
          keepalive: true,
        }).catch(function () {});
      } catch (e) {}
    }
  }

  // ---------------------------------------------------------------------------
  // Trigger primitives
  // ---------------------------------------------------------------------------
  function onDwell(seconds, fn) {
    let fired = false;
    setTimeout(function () { if (!fired) { fired = true; fn(); } }, seconds * 1000);
  }
  function onScroll(percent, fn) {
    let fired = false;
    function check() {
      if (fired) return;
      const h = document.documentElement;
      const scrolled = (window.scrollY + window.innerHeight) / (h.scrollHeight || 1);
      if (scrolled >= percent) { fired = true; fn(); window.removeEventListener('scroll', check); }
    }
    window.addEventListener('scroll', check, { passive: true });
  }
  function onExitIntent(fn) {
    let fired = false;
    document.addEventListener('mouseout', function (e) {
      if (fired) return;
      if (e.clientY <= 5 && (!e.relatedTarget && !e.toElement)) { fired = true; fn(); }
    });
  }
  function onIdle(seconds, fn) {
    let timer; let fired = false;
    function reset() {
      if (fired) return;
      clearTimeout(timer);
      timer = setTimeout(function () { fired = true; fn(); }, seconds * 1000);
    }
    ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'].forEach(function (ev) {
      document.addEventListener(ev, reset, { passive: true });
    });
    reset();
  }

  // ---------------------------------------------------------------------------
  // Surface installers
  // ---------------------------------------------------------------------------
  function installHomepage() {
    if (!eligible('homepage')) return;
    onDwell(30, function () { dispatch('homepage', 'dwell_30s'); });
    onScroll(0.6, function () { dispatch('homepage', 'scroll_60'); });
    onExitIntent(function () { dispatch('homepage', 'exit_intent'); });
  }
  function installPdp() {
    if (!eligible('pdp')) return;
    onDwell(45, function () { dispatch('pdp', 'dwell_45s'); });
    onExitIntent(function () { dispatch('pdp', 'exit_intent'); });
  }
  function installQuiz() {
    if (!eligible('quiz')) return;
    onIdle(90, function () { dispatch('quiz', 'idle_90s'); });
    onExitIntent(function () { dispatch('quiz', 'exit_intent'); });
  }
  function installCart() {
    if (!eligible('cart')) return;
    onExitIntent(function () { dispatch('cart', 'exit_intent'); });
  }

  // ---------------------------------------------------------------------------
  // Template (consumed by UnifiedPopupManager).
  //
  // Manager contract (see veluna-unified-popup-manager.js):
  //   - getTemplate(decision.popupType) must return an object
  //   - renderPopup() calls `template.render(decision)` and expects an HTML
  //     string back
  //   - attachListeners() is called separately by the manager to wire close /
  //     submit handlers; we use a MutationObserver here as a lightweight
  //     binder so the engine remains decoupled from the manager's internals.
  // ---------------------------------------------------------------------------
  function buildHtml(decision) {
    const p = (decision && decision.payload) || {};
    const guide = p.guideTitle || 'The Sleep Foundations Guide';
    // Close elements carry BOTH `data-popup-close` (UnifiedPopupManager hook)
    // and `data-vpc-close` (legacy / standalone fallback). Form has no inline
    // submit handler — the manager attaches one in `attachListeners()` and
    // posts to the subscribe API (/Prod/subscribe) with `popup_type` + `luna_decision`.
    return ''+
      '<div class="veluna-pdf-capture" role="dialog" aria-modal="true" aria-labelledby="vpc-title">'+
      '  <div class="vpc__overlay" data-popup-close data-vpc-close></div>'+
      '  <div class="vpc__card">'+
      '    <button type="button" class="vpc__close" data-popup-close data-vpc-close aria-label="Close">×</button>'+
      '    <div class="vpc__badge"><span class="vpc__badge-num">$47</span><span class="vpc__badge-lbl">Free Gift</span></div>'+
      '    <h2 id="vpc-title" class="vpc__title">'+guide+'</h2>'+
      '    <p class="vpc__sub">A $47 PDF distilled from sleep clinic protocols, sent instantly to your inbox. Yours free — no purchase required.</p>'+
      '    <form class="vpc__form" data-vpc-form data-veluna-email data-source="pdf_capture_'+(decision.surface||'unknown')+'">'+
      '      <label class="vpc__field"><input type="email" name="email" placeholder="your.email@example.com" required autocomplete="email" /></label>'+
      '      <button type="submit" class="vpc__submit">Email me the guide</button>'+
      '    </form>'+
      '    <p class="vpc__privacy">No spam. Unsubscribe anytime.</p>'+
      '  </div>'+
      '</div>';
  }

  window.VelunaPopupTemplates = window.VelunaPopupTemplates || {};
  window.VelunaPopupTemplates.pdf_capture = {
    type: 'pdf_capture',
    render: function (decision) {
      window.__VPC_LAST_DECISION__ = decision;
      return buildHtml(decision);
    },
  };

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------
  // Stylesheet injection — self-contained, no external CSS dependency.
  // The unified popup manager appends `.unified-popup` to <body> and toggles
  // `.unified-popup--visible`; we own those classes here plus the .vpc__*
  // template internals so the popup is always visible without theme CSS.
  // ---------------------------------------------------------------------------
  function injectStyles() {
    if (document.getElementById('vpc-styles')) return;
    const css =
      '.unified-popup{position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;'+
      'opacity:0;visibility:hidden;transition:opacity .25s ease;pointer-events:none;}'+
      '.unified-popup--visible{opacity:1;visibility:visible;pointer-events:auto;}'+
      '.vpc__overlay{position:absolute;inset:0;background:rgba(15,17,22,.72);backdrop-filter:blur(4px);}'+
      '.vpc__card{position:relative;background:#fff;color:#0f1116;max-width:440px;width:calc(100% - 32px);'+
      'border-radius:18px;padding:32px 28px 28px;box-shadow:0 24px 64px rgba(0,0,0,.32);'+
      'font-family:-apple-system,BlinkMacSystemFont,"Inter","Helvetica Neue",sans-serif;'+
      'animation:vpc-in .35s cubic-bezier(.2,.7,.3,1) both;}'+
      '@keyframes vpc-in{from{transform:translateY(12px) scale(.98);opacity:0}to{transform:none;opacity:1}}'+
      '.vpc__close{position:absolute;top:12px;right:14px;background:transparent;border:0;font-size:26px;'+
      'line-height:1;color:#7a8190;cursor:pointer;padding:6px 10px;border-radius:8px;}'+
      '.vpc__close:hover{background:#f1f3f7;color:#0f1116;}'+
      '.vpc__badge{display:inline-flex;align-items:baseline;gap:6px;background:#fef3c7;color:#92400e;'+
      'padding:6px 12px;border-radius:999px;font-size:12px;font-weight:600;margin-bottom:14px;}'+
      '.vpc__badge-num{font-size:14px;font-weight:700;}'+
      '.vpc__title{font-size:24px;line-height:1.2;margin:0 0 10px;font-weight:600;letter-spacing:-.01em;}'+
      '.vpc__sub{font-size:14px;line-height:1.55;color:#4b5263;margin:0 0 20px;}'+
      '.vpc__form{display:flex;flex-direction:column;gap:10px;}'+
      '.vpc__field input{width:100%;padding:12px 14px;border:1px solid #d8dde5;border-radius:10px;'+
      'font-size:15px;color:#0f1116;background:#fafbfc;outline:none;transition:border-color .15s,background .15s;}'+
      '.vpc__field input:focus{border-color:#0f1116;background:#fff;}'+
      '.vpc__submit{padding:13px 16px;background:#0f1116;color:#fff;border:0;border-radius:10px;'+
      'font-size:15px;font-weight:600;cursor:pointer;transition:transform .1s,background .15s;}'+
      '.vpc__submit:hover{background:#1f2330;}'+
      '.vpc__submit:active{transform:translateY(1px);}'+
      '.vpc__privacy{margin:14px 0 0;font-size:11px;color:#8b919c;text-align:center;letter-spacing:.02em;}'+
      '@media(max-width:480px){.vpc__card{padding:26px 22px 22px;border-radius:14px;}.vpc__title{font-size:20px;}}';
    const tag = document.createElement('style');
    tag.id = 'vpc-styles';
    tag.textContent = css;
    document.head.appendChild(tag);
  }

  // ---------------------------------------------------------------------------
  function boot() {
    injectStyles();
    const surface = detectSurface();
    if (!surface) return;
    if (surface === 'homepage') installHomepage();
    // PDP intentionally skipped — veluna-pdp-mystery.js owns this surface
    // (mystery scratch popup with discount + ATC). Running both engines causes
    // popup collision via UnifiedPopupManager's 30-min lockout.
    else if (surface === 'pdp') return;
    else if (surface === 'quiz') installQuiz();
    else if (surface === 'cart') installCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
